import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { FileSignature, Ban } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import api from "@/lib/api";
import { money } from "@/lib/format";

// Belo Rentals rental agreement — must be e-signed before checkout.
export default function RentalAgreementDialog({ open, onOpenChange, car, form, quote, onSign, submitting }) {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(form?.customer_name || "");
    setAgreed(false);
    if (!doc) api.get("/legal/rental-agreement").then((r) => setDoc(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSign = !!name.trim() && agreed && !submitting;
  const miles = quote?.unlimited_miles ? t("agreement.unlimited") : t("agreement.milesIncluded", { miles: (quote?.included_miles || 0).toLocaleString() });
  const facts = [
    [t("agreement.vehicle"), car?.name],
    [t("agreement.renter"), form?.customer_name || name || "—"],
    [t("agreement.tripStart"), form?.pickup_date],
    [t("agreement.tripEnd"), form?.return_date],
    [t("agreement.pickupReturn"), form?.pickup_location],
    [t("agreement.mileage"), miles],
    [t("agreement.refundableDeposit"), money(quote?.deposit || 300)],
    [t("agreement.totalDueNow"), money(quote?.amount_due || 0)],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="agreement-dialog" className="max-w-2xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 text-left">
          <DialogTitle className="font-heading text-xl text-slate-900 flex items-center gap-2">
            <FileSignature size={20} className="text-blue-600" /> {doc?.title || t("agreement.title")}
          </DialogTitle>
          <p className="text-slate-500 text-sm mt-1">{t("agreement.review")}</p>
        </DialogHeader>

        <div className="px-6 py-4 grid grid-cols-2 gap-x-6 gap-y-3 bg-slate-50 border-b border-slate-100">
          {facts.map(([k, v]) => (
            <div key={k} className="flex flex-col">
              <span className="label-caps text-slate-400 text-[10px]">{k}</span>
              <span className="text-slate-900 text-sm font-medium">{v || "—"}</span>
            </div>
          ))}
        </div>

        <ScrollArea className="h-[34vh] px-6 py-4" data-testid="agreement-scroll">
          {doc?.prohibited_uses && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 mb-4">
              <p className="flex items-center gap-1.5 text-rose-700 font-semibold text-sm"><Ban size={15} /> {t("agreement.prohibited")}</p>
              <ul className="list-disc pl-5 mt-1.5 text-rose-700/90 text-xs space-y-0.5">
                {doc.prohibited_uses.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </div>
          )}
          {(doc?.sections || []).map((s, i) => (
            <div key={s.title} className="mb-3.5">
              <h4 className="text-slate-900 font-semibold text-sm">{i + 1}. {s.title}</h4>
              <p className="text-slate-600 text-xs leading-relaxed mt-1">{s.body}</p>
            </div>
          ))}
          {!doc && <p className="text-slate-400 text-sm">{t("agreement.loadingAgreement")}</p>}
        </ScrollArea>

        <div className="px-6 py-4 border-t border-slate-100 space-y-3">
          <label className="block">
            <span className="label-caps text-slate-400 block mb-1.5">{t("agreement.signLabel")}</span>
            <input data-testid="signature-input" value={name} onChange={(e) => setName(e.target.value)} className="belo-input" placeholder={t("agreement.signPlaceholder")} />
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} data-testid="agreement-checkbox" className="mt-0.5" />
            <span className="text-slate-600 text-xs leading-relaxed">
              <Trans i18nKey="agreement.agreeText" values={{ name: name || "________", date: new Date().toLocaleString() }} components={[<b className="text-slate-900" />]} />
            </span>
          </label>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-100 gap-2 sm:gap-2">
          <button type="button" onClick={() => onOpenChange(false)} data-testid="agreement-cancel" className="px-5 py-2.5 rounded-full border-2 border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">{t("agreement.cancel")}</button>
          <button type="button" disabled={!canSign} onClick={() => onSign(name.trim())} data-testid="sign-and-pay" className="px-6 py-2.5 rounded-full bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? t("agreement.redirecting") : t("agreement.signPay", { amount: money(quote?.amount_due || 0) })}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
